// İSG uzmanı portalı — `Giriş` panelinin `T.C. Kimlik` alt-sekmesi: kamerasız/kodsuz yedek yol
// (§29.5 `POST /security/entry/tc-lookup`). Sonuç KARTINI kendisi BASMAZ (S32b) — sonuç
// `useVerdictStage.ts` üzerinden `VerdictPanel`'e (sayfanın TEK verdict yüzeyi) akar; bu bileşen
// yalnız FORMU sunar ve sonucu/ağ hatasını yukarı bildirir.
//
// Aynı TC'nin mükerrer gönderimi bir mutasyon SÜRERKEN bastırılır (tasarım spec'i §3, "Token
// dedupe on the TC path") — her gönderim ayrı bir denetim satırı yazar, çift-ateşleme BEDAVA
// değildir.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Input,
  Spinner,
  type TcLookupResultDto,
  isApiError,
  isValidTcKimlikNo,
  securityApi,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export interface TcLookupTabProps {
  siteUuid: string;
  onResult: (result: TcLookupResultDto) => void;
  onNetworkError: () => void;
}

export function TcLookupTab({ siteUuid, onResult, onNetworkError }: TcLookupTabProps) {
  const { t } = useTranslation();

  const schema = z.object({
    tc: z
      .string()
      .trim()
      .min(1, t('security.tc.validation.required'))
      .refine(isValidTcKimlikNo, t('security.tc.validation.invalid')),
  });
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { tc: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => securityApi.tcLookup({ tc: values.tc.trim(), siteUuid }),
    onSuccess: (result) => {
      onResult(result);
      reset({ tc: '' });
    },
    onError: (error) => {
      if (isApiError(error)) {
        onNetworkError();
      }
    },
  });

  return (
    <div className="flex flex-col gap-4 p-5">
      <form
        className="flex items-end gap-3"
        noValidate
        onSubmit={handleSubmit((values) => {
          if (mutation.isPending) {
            return;
          }
          mutation.mutate(values);
        })}
      >
        <Field
          htmlFor="security-tc"
          label={t('security.tc.field.label')}
          error={formState.errors.tc?.message}
          className="flex-1"
        >
          <Input
            id="security-tc"
            inputMode="numeric"
            autoComplete="off"
            maxLength={11}
            placeholder={t('security.tc.field.placeholder')}
            invalid={Boolean(formState.errors.tc)}
            {...register('tc')}
          />
        </Field>
        <Button type="submit" size="lg" loading={mutation.isPending}>
          {t('security.tc.submit')}
        </Button>
      </form>

      <div className="flex min-h-[8rem] flex-1 items-center justify-center">
        {mutation.isPending ? (
          <Spinner label={t('security.common.loading')} />
        ) : (
          <p className="max-w-sm text-center text-sm text-muted">{t('security.tc.hint')}</p>
        )}
      </div>
    </div>
  );
}
